import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Button,
} from '@mui/material';

import { useEffect, useMemo, useState } from 'react';

import { AdminContent } from '@/components/atoms/AdminPageParts';
import LoadingBackdrop from '@/components/atoms/LoadingBackdrop';
import MyDataGrid from '@/components/atoms/MyDataGrid';
import { useCheckPermission } from '@/components/hooks/useCheckPermission';
import { useSetHeaderTitle } from '@/components/hooks/useHeaderTitle';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import {
  useAdminSettingsQuery,
  useEditAdminSettingsMutation,
} from '@/types/graphql';

type VoiceEngine = 'FEAT' | 'Namisense' | 'Google';

const VoiceEngineSwitch = () => {
  useCheckPermission({ superAdmin: true });
  useSetHeaderTitle('音声認識エンジン切り替え');
  const snackbar = useSnackbar();
  const [editAdminSettings, editAdminSettingsResult] =
    useEditAdminSettingsMutation();

  // NamiSense設定の取得
  const namisenseResult = useAdminSettingsQuery({
    variables: { input: { key: 'available_namisense_languages' } },
  });
  const defaultNamisenseLanguages = useMemo(
    () =>
      namisenseResult.data?.adminSettings[0]?.value
        ?.split(',')
        .filter((x) => x) ?? [],
    [namisenseResult.data?.adminSettings]
  );

  // GCP設定の取得
  const gcpResult = useAdminSettingsQuery({
    variables: { input: { key: 'available_gcp_languages' } },
  });
  const defaultGcpLanguages = useMemo(
    () =>
      gcpResult.data?.adminSettings[0]?.value?.split(',').filter((x) => x) ??
      [],
    [gcpResult.data?.adminSettings]
  );

  const [namisenseLanguages, setNamisenseLanguages] = useState<string[] | null>(
    null
  );
  const [gcpLanguages, setGcpLanguages] = useState<string[] | null>(null);

  useEffect(() => {
    setNamisenseLanguages(defaultNamisenseLanguages);
  }, [defaultNamisenseLanguages]);

  useEffect(() => {
    setGcpLanguages(defaultGcpLanguages);
  }, [defaultGcpLanguages]);

  // 言語タグから音声エンジンを取得
  const getVoiceEngine = (langTag: string): VoiceEngine => {
    if (gcpLanguages?.includes(langTag)) {
      return 'Google';
    } else if (namisenseLanguages?.includes(langTag)) {
      return 'Namisense';
    } else {
      return 'FEAT';
    }
  };

  // 音声エンジンの変更
  const handleChangeVoiceEngine = (langTag: string, engine: VoiceEngine) => {
    // まず両方のリストから削除
    const newNamisenseLanguages = (namisenseLanguages ?? []).filter(
      (lang) => lang !== langTag
    );
    const newGcpLanguages = (gcpLanguages ?? []).filter(
      (lang) => lang !== langTag
    );

    // 選択されたエンジンに応じて追加
    if (engine === 'Namisense') {
      newNamisenseLanguages.push(langTag);
    } else if (engine === 'Google') {
      newGcpLanguages.push(langTag);
    }

    setNamisenseLanguages(newNamisenseLanguages);
    setGcpLanguages(newGcpLanguages);
  };

  // 変更があるかチェック
  const hasChanges =
    namisenseLanguages?.sort().join(',') !==
      defaultNamisenseLanguages?.sort().join(',') ||
    gcpLanguages?.sort().join(',') !== defaultGcpLanguages?.sort().join(',');

  const handleSave = async () => {
    await Promise.all([
      editAdminSettings({
        variables: {
          input: {
            key: 'available_namisense_languages',
            value: (namisenseLanguages ?? []).join(','),
          },
        },
      }),
      editAdminSettings({
        variables: {
          input: {
            key: 'available_gcp_languages',
            value: (gcpLanguages ?? []).join(','),
          },
        },
      }),
    ]);
    namisenseResult.refetch();
    gcpResult.refetch();
    snackbar('success', '保存しました');
  };

  return (
    <Box>
      <LoadingBackdrop
        open={
          namisenseResult.loading ||
          gcpResult.loading ||
          editAdminSettingsResult.loading ||
          namisenseLanguages === null ||
          gcpLanguages === null
        }
      />
      <AdminContent>
        <MyDataGrid
          autoHeight
          hideFooter
          sx={{
            mb: 3,
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#F2F6F8',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'initial',
              lineHeight: 'initial',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              bgcolor: (t) => t.palette.common.white,
            },
          }}
          columns={[
            { field: 'language', headerName: '言語', flex: 1 },
            {
              field: 'voiceEngine',
              headerName: '音声認識エンジン',
              flex: 2,
              renderCell: (cellValues) =>
                cellValues.row.changeable ? (
                  <FormControl>
                    <RadioGroup
                      row
                      value={getVoiceEngine(cellValues.row.langTag)}
                      onChange={(ev) => {
                        handleChangeVoiceEngine(
                          cellValues.row.langTag,
                          ev.target.value as VoiceEngine
                        );
                      }}
                    >
                      <FormControlLabel
                        control={<Radio />}
                        label={'FEAT'}
                        value={'FEAT'}
                      />
                      {cellValues.row.namisenseAvailable && (
                        <FormControlLabel
                          control={<Radio />}
                          label={'Namisense'}
                          value={'Namisense'}
                        />
                      )}
                      <FormControlLabel
                        control={<Radio />}
                        label={'Google'}
                        value={'Google'}
                      />
                    </RadioGroup>
                  </FormControl>
                ) : (
                  cellValues.value
                ),
            },
          ]}
          rows={[
            {
              id: 1,
              language: '日本語',
              langTag: 'ja',
              changeable: true,
              namisenseAvailable: true,
            },
            {
              id: 2,
              language: '英語',
              langTag: 'en',
              changeable: true,
              namisenseAvailable: true,
            },
            {
              id: 3,
              language: 'ベトナム語',
              langTag: 'vi',
              changeable: true,
              namisenseAvailable: true,
            },
            {
              id: 4,
              language: '韓国語',
              langTag: 'ko',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 5,
              language: '中国語（簡体字）',
              langTag: 'zh',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 6,
              language: '中国語（繁体字）',
              langTag: 'zh-TW',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 7,
              language: 'スペイン語',
              langTag: 'es',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 8,
              language: 'フィリピン語',
              langTag: 'fil',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 9,
              language: 'フランス語',
              langTag: 'fr',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 10,
              language: 'インドネシア語',
              langTag: 'id',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 11,
              language: 'ミャンマー語',
              langTag: 'my',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 12,
              language: 'ポルトガル語（ブラジル）',
              langTag: 'pt-BR',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 13,
              language: 'タイ語',
              langTag: 'th',
              changeable: true,
              namisenseAvailable: false,
            },
            {
              id: 14,
              language: 'ネパール語',
              langTag: 'ne',
              changeable: true,
              namisenseAvailable: false,
            },
          ]}
        />
        <Button
          type="submit"
          fullWidth
          disabled={!hasChanges}
          onClick={handleSave}
        >
          保存
        </Button>
      </AdminContent>
    </Box>
  );
};

export default VoiceEngineSwitch;
